import * as CL from '@design-system/component-library';
import { FormProvider, useController, useForm, useFormContext } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import {
  alvZeroMsg,
  cancelMsg,
  chooseOneProductMsg,
  companyMsg,
  companysShareOfMonthlyFeeMsg,
  contractPeriodMsg,
  damageInsuranceCoverMsg,
  damageInsuranceMsg,
  deviceSelectionMsg,
  monthMsg,
  productTypeMsg,
  saveMsg,
  t,
} from '../../common/i18n/index.js';
import { Checkbox } from '../../common/react-hook-form/components/Checkbox.js';
import { Dropdown } from '../../common/react-hook-form/components/Dropdown.js';
import { SelectRadio } from '../../common/react-hook-form/components/SelectRadio.js';
import { Name, TextInput } from '../../common/react-hook-form/fields/index.js';
import { isMultiBiz } from '../../common/utils/accountUtils.js';
import { BillingAccountType } from '../../common/utils/billingAccountUtilsCoCo.js';
import {
  DEVICE_LIST_DAMAGE_INSURANCE_OPTIONS,
  DEVICE_LIST_PRODUCT_TYPES,
  PURCHASED_DEVICE_BILLING_FIELD_PREFIX,
  getCompanyShareKey,
  getContractTermLabel,
  getDeviceListContractTerms,
} from '../../common/utils/deviceListFormUtilsCoCo.js';
import {
  getDeviceSelectionProductTypeLabel,
  getNewDeviceListPath,
} from '../../common/utils/deviceListTabsUtilsCoCo.js';
import { toDeviceSelectionProductType } from '../../common/utils/deviceSelectionListUtilsCoCo.js';
import { EmployeeProductType } from '../../generated/api/employeeProductType.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { CompanySelector } from '../CompanySelector/CompanySelector.js';
import { getUserAccounts } from '../Header/dynamic/headerFunctions.js';
import {
  BillingAccountAdditionalSettingsCoCo,
  CorporateMessageFieldsCoCo,
  DefaultBillingAccountFieldsCoCo,
  EnrollmentProgramFieldsCoCo,
  SectionDisclaimerCoCo,
} from './DeviceListFormSectionsCoCo.js';
import type { DeviceListNewLoaderDataCoCo } from '../../common/loaders.js';
import type { DeviceListFormValuesCoCo } from '../../common/utils/deviceListFormUtilsCoCo.js';
import type { BillingAccountCocoCreationConfig } from '../PlaceOrder/steps/payment/SelectBillingAccountCoCo.js';
import type { ReactNode } from 'react';

import '../CatalogConfiguration/CatalogConfigurationForm.scss';
import './DeviceListFormCoCo.scss';

const SectionDivider = () => <hr className={dsClass.MARGIN_VERTICAL_3} />;

// Same billing account creation as in the checkout (PlaceOrderCoCo)
const getBillingAccountCreationConfig = ({
  billChannels,
  customer,
}: DeviceListNewLoaderDataCoCo): BillingAccountCocoCreationConfig => ({
  billingAccountType: BillingAccountType.SFDC,
  billChannels,
  customer,
  relatedParties: (customer.relatedParty ?? []).map(({ id, role, name, referredType }) => ({
    id,
    role,
    name,
    referredType,
  })),
});

const BillingSection = ({ children }: { children: ReactNode }) => (
  <div className={`device-list-form-coco__bordered-box ${dsClass.MARGIN_BOTTOM_3} ${dsClass.PADDING_4}`}>
    {children}
  </div>
);

const ContractTermFields = ({ productType }: { productType: EmployeeProductType }) => {
  const { control } = useFormContext();
  const { field, fieldState, formState } = useController({
    name: 'catalog.contractTerms',
    control,
    rules: {
      validate: (terms?: string[]) => (terms?.length ? undefined : t.OW64(chooseOneProductMsg)),
    },
  });
  const contractTerms: string[] = field.value ?? [];

  return (
    <div>
      <h4>{t.ULI0(contractPeriodMsg)}</h4>
      {getDeviceListContractTerms(productType).map(contractTerm => {
        const isChecked = contractTerms.includes(contractTerm);
        return (
          <div key={`${productType}-${contractTerm}`} className={dsClass.MARGIN_BOTTOM_2}>
            <CL.Checkbox
              id={`device-list-contract-term-${contractTerm}`}
              checked={isChecked}
              label={getContractTermLabel(contractTerm)}
              onChange={e =>
                field.onChange(
                  e.currentTarget.checked
                    ? [...contractTerms, contractTerm]
                    : contractTerms.filter(term => term !== contractTerm)
                )
              }
            />
            {productType === EmployeeProductType.EPP && isChecked && (
              <div className="flex">
                <TextInput
                  label={`${t.H8Q4(companysShareOfMonthlyFeeMsg)} (${t.S8TX(alvZeroMsg)})`}
                  min={0}
                  name={`catalog.companyShare.${getCompanyShareKey(contractTerm)}`}
                  required={false}
                  className="width-50"
                  type="number"
                  placeholder=""
                >
                  <span className="extension">€/{t.XXVX(monthMsg)}</span>
                </TextInput>
              </div>
            )}
          </div>
        );
      })}
      {formState.isSubmitted && fieldState.error && <CL.InputError>{fieldState.error.message}</CL.InputError>}
    </div>
  );
};

export interface DeviceListFormCoCoProps {
  data: DeviceListNewLoaderDataCoCo;
  initialValues?: DeviceListFormValuesCoCo;
  onSubmit: (values: DeviceListFormValuesCoCo) => void;
  cancelHref: string;
  // The company of an existing list can't be changed
  isCompanySelectable?: boolean;
}

export const DeviceListFormCoCo = ({
  data,
  initialValues,
  onSubmit,
  cancelHref,
  isCompanySelectable = true,
}: DeviceListFormCoCoProps) => {
  const navigate = useNavigate();
  const { authenticatedUser } = useAuth();
  const methods = useForm<DeviceListFormValuesCoCo>({
    mode: 'all',
    shouldUnregister: true,
    defaultValues: initialValues ?? {
      catalog: {
        name: '',
        productType: EmployeeProductType.EPP,
        employeeProductSelection: '',
        contractTerms: [],
        showPurchaseDevicePrice: false,
        damageInsurance: [],
        selectedBillingAccountIds: [],
      },
    },
  });
  const productType = methods.watch('catalog.productType');
  const isEpp = productType === EmployeeProductType.EPP;
  const useDifferentPurchasedDeviceBillingAccount = methods.watch('catalog.useDifferentPurchasedDeviceBillingAccount');
  const deviceSelectionItems = data.deviceSelections
    .filter(deviceSelection => deviceSelection.productType === productType)
    .map(deviceSelection => ({ label: deviceSelection.name, value: deviceSelection.id }));
  const billingAccountCreation = getBillingAccountCreationConfig(data);

  return (
    <FormProvider {...methods}>
      <form className="catalog-configuration" onSubmit={methods.handleSubmit(onSubmit)} noValidate>
        {isCompanySelectable && isMultiBiz(authenticatedUser) && (
          <div className="device-list-form-coco__field">
            <CompanySelector
              className={dsClass.MARGIN_BOTTOM_4}
              userAccounts={getUserAccounts(authenticatedUser)}
              onInputChange={(_, option) =>
                option.accountMasterId && navigate(getNewDeviceListPath(option.accountMasterId), { replace: true })
              }
              initialMdmId={data.mdmId}
              labelText={t.KJTS(companyMsg)}
            />
          </div>
        )}
        <div className="device-list-form-coco__field">
          <Name
            name="catalog.name"
            label={t.IVJB('Name of device list')}
            placeholder={t.IVJB('Name of device list')}
            maxLength={65}
            hint={t.WYPE('Max 65 characters')}
            allowSpecialCharacters={true}
          />
        </div>

        <SectionDivider />
        <SelectRadio
          name="catalog.productType"
          label={t.R6HP(productTypeMsg)}
          items={DEVICE_LIST_PRODUCT_TYPES.map(type => ({
            label: getDeviceSelectionProductTypeLabel(toDeviceSelectionProductType(type)),
            value: type,
          }))}
          onChange={() => {
            methods.setValue('catalog.employeeProductSelection', '');
            methods.setValue('catalog.contractTerms', []);
          }}
        />
        <SectionDivider />
        {deviceSelectionItems.length > 0 ? (
          <div className="device-list-form-coco__field">
            <Dropdown
              id="device-list-device-selection"
              name="catalog.employeeProductSelection"
              label={t.AHB4(deviceSelectionMsg)}
              items={deviceSelectionItems}
            />
          </div>
        ) : (
          <>
            <h4>{t.AHB4(deviceSelectionMsg)}</h4>
            <p>{t.OW10('No device selections')}</p>
          </>
        )}
        <SectionDivider />
        <ContractTermFields productType={productType} />
        <SectionDivider />
        {!isEpp && (
          <>
            <h4>{t.JIID('Purchased device price')}</h4>
            <Checkbox
              name="catalog.showPurchaseDevicePrice"
              label={t.L7VP('Show purchased device price to employees')}
            />
            <CL.Disclaimer
              className={dsClass.MARGIN_TOP_3}
              disclaimerType="info"
              icon={<CL.Icon icon="information" type="regular" aria-hidden="true" />}
              visible
              text={t.T197(
                'When enabled, employees can see the company cost of purchased devices (accessories and other devices paid with one-time fee).'
              )}
            />
            <SectionDivider />
          </>
        )}
        {isEpp && (
          <>
            <h4>{t.QSXP(damageInsuranceMsg)}</h4>
            {DEVICE_LIST_DAMAGE_INSURANCE_OPTIONS.map(({ label, value }) => (
              <Checkbox key={value} name="catalog.damageInsurance" label={label} value={value} />
            ))}
            <SectionDisclaimerCoCo>
              <p className={dsClass.MARGIN_0}>{t.FLLT(damageInsuranceCoverMsg)}</p>
            </SectionDisclaimerCoCo>
            <SectionDivider />
          </>
        )}
        <CorporateMessageFieldsCoCo />
        <SectionDivider />
        <EnrollmentProgramFieldsCoCo />
        <SectionDivider />
        <BillingSection>
          <DefaultBillingAccountFieldsCoCo
            heading={t.IXED('Device catalog billing information')}
            fieldPrefix="catalog"
            mdmId={data.mdmId}
            billingAccounts={data.billingAccounts}
            createBillingAccount={billingAccountCreation}
          />
          {isEpp && (
            <Checkbox
              className={dsClass.MARGIN_BOTTOM_3}
              name="catalog.useDifferentPurchasedDeviceBillingAccount"
              label={t.I0YR('Use different billing account for purchased devices')}
            />
          )}
          <BillingAccountAdditionalSettingsCoCo
            fieldPrefix="catalog"
            billingAccounts={data.billingAccounts}
            defaultSelectedBillingAccountIds={initialValues?.catalog.selectedBillingAccountIds}
          />
        </BillingSection>
        {isEpp && useDifferentPurchasedDeviceBillingAccount && (
          <BillingSection>
            <DefaultBillingAccountFieldsCoCo
              heading={t.DEPW('Billing information for purchased devices')}
              fieldPrefix={PURCHASED_DEVICE_BILLING_FIELD_PREFIX}
              mdmId={data.mdmId}
              billingAccounts={data.billingAccounts}
            />
            <BillingAccountAdditionalSettingsCoCo
              fieldPrefix={PURCHASED_DEVICE_BILLING_FIELD_PREFIX}
              billingAccounts={data.billingAccounts}
              defaultSelectedBillingAccountIds={
                initialValues?.catalog.purchasedDeviceBilling?.selectedBillingAccountIds
              }
            />
          </BillingSection>
        )}

        <div className={`${dsClass.PADDING_VERTICAL_2} device-list-form-coco__actions`}>
          <Link
            to={cancelHref}
            className={`${dsClass.LINK} ${dsClass.LINK_STYLE_LINK_BUTTON} ${dsClass.LINK_BUTTON_COLOR_LINK} ${dsClass.LINK_BUTTON_SIZE_L}`}
          >
            {t.B2V1(cancelMsg)}
          </Link>
          <CL.Button size="l" type="submit" loading={methods.formState.isSubmitting}>
            {t.NIWE(saveMsg)}
          </CL.Button>
        </div>
      </form>
    </FormProvider>
  );
};
